import React, { useCallback, useEffect, useState } from 'react';
import { useDataProvider, Link } from 'react-admin';
import { useFormContext, useWatch } from 'react-hook-form';
import {
  Box,
  Button,
  List,
  ListItemButton,
  ListItemText,
  Typography,
  Chip,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { resolveStockEditId } from '../../../utils/eventSessions';
import { computeEventStatus, EVENT_STATUS } from '../../../utils/eventStatus';
import {
  StandardProductFormComponent,
  SeatedProductFormComponent,
} from '../../stocks/StocksFormComponents';
import EmptyList from '../../misc/EmptyList';
import { getEventDbId } from '../../../utils/eventIdentity';
import { getStocks } from '../../../services/stocksService';
import { formatStockDateTime } from '../../../utils/stocksUtils';

export const STOCK_FORM_PREFIX = '_stockRecord.';

/**
 * Stock edit tab embedded in event edit.
 * - oneoff: tek stock product form
 * - session: oturum listesi → secilen stock product form
 */
export const EventStockEditTab = ({ record, dateType }) => {
  const dataProvider = useDataProvider();
  const { setValue, getValues } = useFormContext();
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [sessionStocks, setSessionStocks] = useState([]);
  /** session: henuz stock secilmedi / secim listesi goster */
  const [pickingSession, setPickingSession] = useState(dateType === 'session');
  const stockRecord = useWatch({ name: '_stockRecord' });

  const hasEventPassed =
    record && computeEventStatus(record).key === EVENT_STATUS.ENDED;
  const eventDbId = getEventDbId(record);
  const isSession = dateType === 'session';

  const loadStockById = useCallback(
    async (stockId) => {
      if (!stockId) return false;
      const { data } = await dataProvider.getOne('stocks', { id: stockId });
      setValue('_stockRecord', data, { shouldDirty: false });
      return true;
    },
    [dataProvider, setValue],
  );

  useEffect(() => {
    if (!record?.id) {
      setLoading(false);
      return undefined;
    }

    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setLoadError(null);
      try {
        if (isSession) {
          const stocks = await getStocks({
            eventID: eventDbId || record.id,
            limitCount: 200,
          });
          if (cancelled) return;
          setSessionStocks(Array.isArray(stocks) ? stocks : []);

          if (!stocks?.length) {
            setLoadError('no_stock');
            setPickingSession(true);
            return;
          }

          // Tek oturum → dogrudan product form
          if (stocks.length === 1) {
            await loadStockById(stocks[0].id);
            if (!cancelled) setPickingSession(false);
            return;
          }

          // Cok oturum: kayitli secim varsa koru, yoksa liste
          const existing = getValues('_stockRecord');
          if (existing?.id && stocks.some((s) => s.id === existing.id)) {
            if (!cancelled) setPickingSession(false);
            return;
          }

          setValue('_stockRecord', null, { shouldDirty: false });
          if (!cancelled) setPickingSession(true);
          return;
        }

        // oneoff
        const existing = getValues('_stockRecord');
        if (existing?.id) {
          if (!cancelled) setPickingSession(false);
          return;
        }
        const stockIdResolved = await resolveStockEditId(record);
        if (!stockIdResolved) {
          if (!cancelled) setLoadError('no_stock');
          return;
        }
        await loadStockById(stockIdResolved);
        if (!cancelled) setPickingSession(false);
      } catch (error) {
        console.error('EventStockEditTab:', error);
        if (!cancelled) setLoadError('load_failed');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [
    record,
    dateType,
    isSession,
    eventDbId,
    dataProvider,
    setValue,
    getValues,
    loadStockById,
  ]);

  const handlePickSession = async (stock) => {
    if (!stock?.id) return;
    setLoading(true);
    setLoadError(null);
    try {
      await loadStockById(stock.id);
      setPickingSession(false);
    } catch (error) {
      console.error('EventStockEditTab pick:', error);
      setLoadError('load_failed');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <EmptyList mode="loading" />;
  }

  if (loadError === 'no_stock') {
    return (
      <p style={{ padding: '8px 0' }}>
        No stock record is linked to this event yet.
      </p>
    );
  }

  if (loadError === 'load_failed') {
    return (
      <p style={{ padding: '8px 0', color: '#b45309' }}>
        Could not load stock for this event. Try again or use the stocks list.
      </p>
    );
  }

  // Session multi-stock picker
  if (isSession && pickingSession) {
    return (
      <Box sx={{ py: 1 }}>
        <Typography variant="body1" sx={{ mb: 1 }}>
          Bu etkinlikte birden fazla oturum (stock) var. Ürünleri düzenlemek için
          bir oturum seçin.
        </Typography>
        {sessionStocks.length === 0 ? (
          <Typography color="text.secondary">Oturum bulunamadı.</Typography>
        ) : (
          <List dense sx={{ border: '1px solid #e5e7eb', borderRadius: 1 }}>
            {sessionStocks.map((s) => (
              <ListItemButton
                key={s.id}
                onClick={() => handlePickSession(s)}
                divider
              >
                <ListItemText
                  primary={formatStockDateTime(s.date, s.time) || s.id}
                  secondary={
                    <>
                      ID: {s.id}
                      {s.isActive === false ? ' · inactive' : ''}
                      {typeof s.sales === 'number' ? ` · ${s.sales} sales` : ''}
                    </>
                  }
                />
                <Chip size="small" label="Ürünleri aç" color="primary" variant="outlined" />
              </ListItemButton>
            ))}
          </List>
        )}
        {eventDbId && (
          <Box sx={{ mt: 2 }}>
            <Link to={`/stocks?eventID=${eventDbId}`}>Stocks listesine git</Link>
          </Box>
        )}
      </Box>
    );
  }

  if (!stockRecord?.id) {
    return (
      <Box sx={{ py: 1 }}>
        <Typography color="text.secondary">Stock yüklenemedi.</Typography>
        {isSession && (
          <Button
            size="small"
            sx={{ mt: 1 }}
            onClick={() => setPickingSession(true)}
          >
            Oturum listesine dön
          </Button>
        )}
      </Box>
    );
  }

  if (hasEventPassed) {
    return (
      <p style={{ padding: '8px 0', color: '#6b7280' }}>
        This event has ended. Stock cannot be edited.
      </p>
    );
  }

  const useSeatedForm =
    stockRecord.type === 3 ||
    Boolean(stockRecord.seatSelections) ||
    Array.isArray(stockRecord.stock);

  return (
    <React.Fragment>
      {isSession && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            mb: 2,
            pb: 1,
            borderBottom: '1px solid #e5e7eb',
          }}
        >
          {sessionStocks.length > 1 && (
            <Button
              size="small"
              startIcon={<ArrowBackIcon />}
              onClick={() => {
                setPickingSession(true);
              }}
            >
              Oturumlar
            </Button>
          )}
          <Typography variant="body2" color="text.secondary">
            Oturum:{' '}
            <strong>
              {formatStockDateTime(stockRecord.date, stockRecord.time) ||
                stockRecord.id}
            </strong>
          </Typography>
        </Box>
      )}

      {!useSeatedForm ? (
        <StandardProductFormComponent
          stock={stockRecord}
          formPrefix={STOCK_FORM_PREFIX}
          sourceLang={record?.sourceLanguage || 'tr'}
        />
      ) : (
        <SeatedProductFormComponent
          stock={stockRecord}
          formPrefix={STOCK_FORM_PREFIX}
          sourceLang={record?.sourceLanguage || 'tr'}
        />
      )}
    </React.Fragment>
  );
};
